/**
 * How a signed-in human is NAMED on screen — pure, so the rule is testable
 * and identical wherever it renders.
 *
 * The rule is the backend's own (identity-directory): the Auth profile's
 * displayName, else its username. Nothing is composed from first/last names
 * here — every other attribution surface in the product reads the same two
 * fields, and a name assembled differently on the account page than on a
 * roster would be a second identity.
 */

import type { MyProfile } from './api';

export function displayNameOf(
  profile: Pick<MyProfile, 'displayName' | 'username'> | null | undefined,
): string | null {
  if (!profile) return null;
  const name = profile.displayName?.trim();
  if (name) return name;
  const username = profile.username?.trim();
  return username ? username : null;
}

/**
 * Avatar initials from a human name: the first letter of the first two
 * words. Works for Persian (no case to change) and Latin alike; a single
 * word yields one letter. No name → an en dash, never the uid's "sp".
 */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '–';
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, 2).map((w) => [...w][0] ?? '');
  const out = letters.join('');
  return out ? out.toLocaleUpperCase() : '–';
}
